import { useState, useEffect } from 'react';
import { Box, Collapse } from '@mui/material';
import StandardErrorAlert from '../../../../components/alerts/StandardErrorAlert';
import CenteredFlexContainer from '../../../../components/containers/CenteredFlexContainer';
import MediumCircularProgress from '../../../../components/progressBar/MediumCircularProgress';
import FullPageCircularProgress from '../../../../components/progressBar/FullPageCircularProgress';
import InfiniteScroll from '../../../../components/common/InfiniteScroll';
import Header from './Header';
import ContentTabs from './ContentTabs';
import SearchFilterBar from './SearchFilterBar';
import ContentDisplay from './ContentDisplay';
import CreateReadingPractice from '../CreateReadingPractice';
import { useReadingPracticeSearch } from '../../hooks/useReadingPracticeSearch';

const ListReadingPractice = () => {
  const {
    loading,
    error,
    searchTerm,
    selectedCategories,
    selectedGradeLevels,
    viewType,
    tabValue,
    showCreateForm,
    hasMore,
    submitSearchParams,
    setshowCreateForm,
    loadMore,
    handleTabChange,
    handleViewTypeChange,
    handleContentGenerated,
    getTabContents,
    // Import all tab constants from the hook
    draftTab,
    publishTab,
    approvedTab,
    releasedTab,
    archivedTab,
    readingCategories
  } = useReadingPracticeSearch();

  // Local state that tracks the current input values before submission
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [localSelectedCategories, setLocalSelectedCategories] = useState([]);
  const [localSelectedGradeLevels, setLocalSelectedGradeLevels] = useState([]);
  
  // Keep local state in sync with the hook's values
  useEffect(() => {
    setLocalSearchTerm(searchTerm || '');
    setLocalSelectedCategories(selectedCategories || []);
    setLocalSelectedGradeLevels(selectedGradeLevels || []);
  }, [searchTerm, selectedCategories, selectedGradeLevels]);

  const handleSearchSubmit = (term, categories, gradeLevels) => {
    // Use the provided values or fallback to current local state
    const searchValue = term !== undefined ? term : localSearchTerm;
    const categoriesValue = categories !== undefined ? categories : localSelectedCategories;
    const gradeLevelsValue = gradeLevels !== undefined ? gradeLevels : localSelectedGradeLevels;
    
    // Submit to the hook which will handle the API call
    submitSearchParams(searchValue, categoriesValue, gradeLevelsValue);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearchSubmit(localSearchTerm, localSelectedCategories, localSelectedGradeLevels);
    }
  };

  const handleClearSearch = () => {
    submitSearchParams('', [], []);
  };

  const handleTabChangeWithSearchUpdate = (event, newValue) => {
    handleTabChange(event, newValue);
  };

  if (loading && getTabContents()?.length === 0) {
    return (<FullPageCircularProgress />);
  }

  const tabContents = getTabContents() || [];

  // Create tab constants object to pass to ContentDisplay with all tabs
  const tabConstants = {
    draftTab,
    publishTab,
    approvedTab,
    releasedTab,
    archivedTab
  };

  return (
    <Box>
      <Header onCreateNew={() => setshowCreateForm(true)} />
      {error && (<StandardErrorAlert>{error}</StandardErrorAlert>)}
      
      <Collapse in={showCreateForm} mountOnEnter unmountOnExit>
        <Box sx={{ mb: 3 }}>
          <CreateReadingPractice onCancel={() => setshowCreateForm(false)} onContentGenerated={handleContentGenerated} />
        </Box>
      </Collapse>

      <ContentTabs
        value={tabValue}
        onChange={handleTabChangeWithSearchUpdate} />

      <SearchFilterBar
        searchTerm={localSearchTerm}
        selectedGrades={localSelectedGradeLevels}
        selectedCategories={localSelectedCategories}
        readingCategories={readingCategories}
        viewType={viewType}
        onSearchChange={setLocalSearchTerm}
        onGradesChange={setLocalSelectedGradeLevels}
        onCategoriesChange={setLocalSelectedCategories}
        onSearchSubmit={handleSearchSubmit}
        onKeyPress={handleKeyPress}
        onViewTypeChange={handleViewTypeChange} />

      <InfiniteScroll
        loading={loading}
        hasMore={hasMore}
        onLoadMore={loadMore}>

        <Box key={tabValue} className="page-transition-enter">
          <ContentDisplay
            contents={tabContents}
            searchTerm={localSearchTerm}
            viewType={viewType}
            tabValue={tabValue}
            onClearSearch={handleClearSearch}
            onCreateContent={() => setshowCreateForm(true)}
            loading={loading}
            readingCategories={readingCategories}
            tabConstants={tabConstants} />
        </Box>

        {loading && tabContents.length > 0 && (
          <CenteredFlexContainer sx={{ py: 4 }}>
            <MediumCircularProgress />
          </CenteredFlexContainer>
        )}
      </InfiniteScroll>
    </Box>
  );
};

export default ListReadingPractice;